import type { ActivityEvent } from './activitySlice'
import { AGENT_USER_ID, AGENT_NAME } from '@/const/constants'

// Resolve a member id to the name shown in activity text — the agent reads as "Plate".
function resolveName(id: string | null | undefined, getMember?: (id: string) => { username?: string } | undefined): string {
    if (!id) return 'someone'
    if (id === AGENT_USER_ID) return AGENT_NAME
    return getMember?.(id)?.username ?? 'someone'
}

// ─── Date grouping ────────────────────────────────────────────────────────────

export function getDayKey(timestamp: any): string {
    if (!timestamp?.seconds) return 'unknown'
    const d = new Date(timestamp.seconds * 1000)
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

export function getDayLabel(timestamp: any): string {
    if (!timestamp?.seconds) return 'Earlier'
    const date = new Date(timestamp.seconds * 1000)
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const diff = todayStart - dayStart
    if (diff === 0) return 'Today'
    if (diff === 86400000) return 'Yesterday'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function groupByDay(events: ActivityEvent[]): { label: string; events: ActivityEvent[] }[] {
    const map = new Map<string, { label: string; events: ActivityEvent[] }>()
    for (const event of events) {
        const key = getDayKey(event.createdAt)
        if (!map.has(key)) map.set(key, { label: getDayLabel(event.createdAt), events: [] })
        map.get(key)!.events.push(event)
    }
    return Array.from(map.values())
}

// ─── Action text ──────────────────────────────────────────────────────────────

interface ActionTextOpts {
    statuses?: { id: string; name: string }[]
    getMember?: (id: string) => { username?: string } | undefined
    fromListName?: string
    toListName?: string
    // task context: "created this task" instead of "created"
    taskContext?: boolean
}

export function getActivityActionText(event: ActivityEvent, opts: ActionTextOpts = {}): string {
    const { type, meta } = event
    const { statuses = [], getMember, fromListName, toListName, taskContext = false } = opts

    if (type === 'project_created') return `created project "${meta.projectName ?? ''}"`
    if (type === 'project_deleted') return `deleted project "${meta.projectName ?? ''}"`
    if (type === 'task_created') return taskContext ? 'created this task' : 'created'
    if (type === 'task_deleted') return taskContext ? 'deleted this task' : 'deleted'

    if (type === 'task_status_changed') {
        const toStatus = statuses.find(s => s.id === meta.toStatusId)
        return toStatus ? `changed status to ${toStatus.name}` : 'changed status'
    }

    if (type === 'task_assigned') {
        if (!meta.toAssigneeId) {
            return `unassigned ${resolveName(meta.fromAssigneeId, getMember)}`
        }
        return `assigned to ${resolveName(meta.toAssigneeId, getMember)}`
    }

    if (type === 'task_renamed') return 'renamed this task'
    if (type === 'task_description_changed') return taskContext ? 'updated the description' : 'updated a description'
    if (type === 'task_labels_changed') return 'updated labels'

    if (type === 'task_moved') {
        const from = fromListName || (meta.fromListId ? 'Section' : '?')
        const to = toListName || (meta.toListId ? 'Section' : '?')
        return taskContext ? `moved from ${from} to ${to}` : `moved to ${to}`
    }

    if (type === 'comment_added') return taskContext ? 'commented' : 'commented'

    return type
}
